<script setup lang="ts">
import { ref, type Ref } from 'vue';
import { useClickOutside } from '@/composables/clickOutside';
import ColorSelector from '../ColorSelector.vue';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';

defineProps<{
    label: string;
    color?: ColorObject;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    (e: 'color-selected', color: FontColorOption): void;
}>();

const pickerRef: Ref<HTMLElement | undefined> = ref();

const { clickOutsideActive } = useClickOutside(
    pickerRef,
    () => {
        clickOutsideActive.value = false;
    },
    false
);

const togglePicker = () => {
    if (!clickOutsideActive.value) clickOutsideActive.value = true;
    else clickOutsideActive.value = false;
};

const onColorSelected = (color: FontColorOption) => {
    emit('color-selected', color);
    clickOutsideActive.value = false;
};
</script>

<template>
    <section class="qr-color-picker">
        <h6>{{ label }}</h6>
        <div
            class="color-row border-gray-light rounded"
            :class="{ disabled: disabled }"
            @click="!disabled && togglePicker()"
        >
            <span class="color-preview" :style="{ backgroundColor: color?.hex || 'transparent' }" />
            {{ color?.hex || 'Transparent' }}
        </div>
        <color-selector
            ref="pickerRef"
            :open="clickOutsideActive"
            :color="color"
            @cancel="clickOutsideActive = false"
            @color-selected="onColorSelected"
        />
    </section>
</template>

<style scoped lang="scss">
.qr-color-picker {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    h6 {
        font-size: 0.875rem;
        margin-bottom: 0;
    }

    .color-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem;
        cursor: pointer;

        &.disabled {
            cursor: not-allowed;
            opacity: 0.6;
        }

        .color-preview {
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 0.125rem;
        }
    }
}
</style>
